/**
 * Something a person put in the memo — words, a table cell, a typed figure —
 * as a link in the person's colour (DOP-637, DOP-640). A found value opens
 * its page; this opens the note that is its source (a call, an email, a
 * board pack) with who added it and when. There is no file to open.
 *
 * The card leads with the source, in bold; who and when are its footnote,
 * in grey. It sits at the document root beside the value, so a table's own
 * scroll box cannot clip it: below the value when it fits, above it when it
 * does not, never off the screen. A click anywhere else, or Escape, closes it.
 */

import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { formatIsoDay } from "./dealScreeningFormat";

export interface PersonNoteProps {
	children: React.ReactNode;
	note: string | null;
	/** Who wrote or entered it, when known. */
	by?: string | null;
	/** ISO date or date-time it was entered. */
	at?: string | null;
	/** Kept for callers; the card places itself where it fits. */
	align?: "left" | "right";
	/** "Added" for words, "Entered" for a figure, "Marked not found" for an accepted gap. */
	verb?: string;
	testId?: string;
	/** The card's name for a screen reader: "Who entered Revenue". */
	label?: string;
	/** Under the attribution: an Edit that reopens a person's answer. */
	footer?: React.ReactNode;
	/** Test id on the attribution line (the page's tests name it). */
	attributionTestId?: string;
}

const PANEL_WIDTH = 280;
const GUTTER = 8;

const PersonNote: React.FC<PersonNoteProps> = ({
	children,
	note,
	by = null,
	at = null,
	verb = "Added",
	testId,
	label,
	footer,
	attributionTestId,
}) => {
	const [open, setOpen] = useState(false);
	const [place, setPlace] = useState<{ top: number; left: number } | null>(null);
	const buttonRef = useRef<HTMLButtonElement>(null);
	const panelRef = useRef<HTMLDivElement>(null);

	const rePlace = useCallback(() => {
		const rect = buttonRef.current?.getBoundingClientRect();
		if (!rect) return;
		const height = panelRef.current?.offsetHeight ?? 0;
		const left = Math.max(GUTTER, Math.min(rect.left, window.innerWidth - PANEL_WIDTH - GUTTER));
		const below = rect.bottom + 4;
		const fitsBelow = below + height <= window.innerHeight - GUTTER;
		const top = fitsBelow ? below : Math.max(GUTTER, rect.top - 4 - height);
		setPlace((was) => (was && was.top === top && was.left === left ? was : { top, left }));
	}, []);
	// Once the card is on screen its height is known: place it again.
	const placed = place !== null;
	useLayoutEffect(() => {
		if (open && placed) rePlace();
	}, [open, placed, rePlace]);
	useLayoutEffect(() => {
		if (!open) {
			setPlace(null);
			return undefined;
		}
		rePlace();
		window.addEventListener("resize", rePlace);
		document.addEventListener("scroll", rePlace, true);
		return () => {
			window.removeEventListener("resize", rePlace);
			document.removeEventListener("scroll", rePlace, true);
		};
	}, [open, rePlace]);
	useEffect(() => {
		if (!open) return undefined;
		const onKeyDown = (event: KeyboardEvent) => {
			if (event.key === "Escape") {
				setOpen(false);
				buttonRef.current?.focus();
			}
		};
		const onPointerDown = (event: MouseEvent) => {
			const target = event.target as Node | null;
			if (!target || panelRef.current?.contains(target) || buttonRef.current?.contains(target)) return;
			setOpen(false);
		};
		document.addEventListener("keydown", onKeyDown);
		document.addEventListener("mousedown", onPointerDown);
		return () => {
			document.removeEventListener("keydown", onKeyDown);
			document.removeEventListener("mousedown", onPointerDown);
		};
	}, [open]);

	return (
		<span className="relative inline align-baseline">
			<button
				type="button"
				ref={buttonRef}
				aria-haspopup="dialog"
				aria-expanded={open}
				title={note ? `Source: ${note}` : undefined}
				data-testid={testId}
				onClick={() => setOpen((was) => !was)}
				className="text-left text-[#5B3FA8] underline decoration-[#a48ff0] decoration-dotted decoration-1 underline-offset-2 hover:decoration-[#5B3FA8]"
			>
				{children}
			</button>
			{open &&
				place &&
				createPortal(
					<div
						ref={panelRef}
						role="dialog"
						aria-label={label ?? "Where this is from"}
						// Measured against the value, so placed on the element; a long
						// note scrolls inside the card, never past the screen.
						style={{
							top: place.top,
							left: place.left,
							width: PANEL_WIDTH,
							maxHeight: `calc(100vh - ${2 * GUTTER}px)`,
						}}
						className="fixed z-50 space-y-1 overflow-y-auto rounded-lg border border-[#e7e7e4] bg-white p-2.5 text-left font-sans text-[12.5px] font-normal not-italic leading-snug shadow-lg"
						data-testid={testId ? `${testId}-note` : undefined}
					>
						<p className="break-words font-semibold text-[#111111]">
							{note ? `Source: ${note}` : "No source given"}
						</p>
						{(by || at) && (
							<p className="break-words text-[#6B7280]" data-testid={attributionTestId}>
								{verb}
								{by ? ` by ${by}` : ""}
								{at ? `, ${formatIsoDay(at)}` : ""}
							</p>
						)}
						{footer}
					</div>,
					document.body,
				)}
		</span>
	);
};

export default PersonNote;
